import { coreComponents } from '@ayumia/contracts/components';
/**
 * Registre fermé des primitives (58 §3 niveau 1, 59 §22 « UI Core »).
 *
 * Une primitive est un nom du design system, pas un composant React : le renderer d'`apps/web` en
 * fournit l'implémentation, le manifeste ne la voit jamais. Le registre dit, pour chaque primitive,
 * quelles propriétés, quels événements et si elle accepte des enfants. Il est fermé : une primitive
 * nouvelle s'ajoute ici, avec son rendu, jamais depuis un Pack.
 *
 * `visible`, `disabled`, `readOnly` et `opacity` sont de la présentation (58 §12) ; aucune
 * propriété de ce registre ne décide d'un accès.
 */
/** Propriétés visuelles de 58 §11, admises dans `style` de toute primitive ; aucun CSS libre. */
export const VISUAL_PROPERTIES = new Set([
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'lineHeight',
    'letterSpacing',
    'color',
    'backgroundColor',
    'border',
    'borderRadius',
    'margin',
    'padding',
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'opacity',
    'visibility',
    'zIndex',
    'alignment',
    'gap',
    'shadow',
]);
/**
 * Composants Core de vue (59 §5, §7) : rendus par la page de vue, jamais placés dans le gabarit d'un
 * composite. `Table` est aussi une primitive de gabarit (58 §3).
 */
export const VIEW_ONLY_COMPONENTS = new Set(['Collection', 'CardList', 'Kanban', 'Agenda', 'WeekGrid', 'Detail', 'Form', 'Dashboard']);
/** Blocs de formulaire (ADR-146 §6.2) : placés par la couche de mise en page d'un formulaire, jamais dans un gabarit de composite. */
export const FORM_BLOCK_COMPONENTS = new Set(['ObjectBanner', 'FormSummary', 'FormImage', 'ActionButton', 'CounterChip']);
/**
 * Projection du registre des composants Core (ADR-099, `docs/contracts/components`) : propriétés =
 * entrées déclarées, événements = sorties déclarées, enfants admis si le manifeste le dit. Le
 * registre est la seule source ; une primitive nouvelle s'y ajoute, avec son renderer.
 */
export const PRIMITIVES = Object.fromEntries(Object.entries(coreComponents)
    .filter(([name]) => !VIEW_ONLY_COMPONENTS.has(name) && !FORM_BLOCK_COMPONENTS.has(name))
    .map(([name, manifest]) => [
    name,
    { props: new Set(Object.keys(manifest.inputs ?? {})), events: new Set(Object.keys(manifest.outputs ?? {})), children: manifest.accepts_children === true },
]));
export function primitiveDefinition(name) {
    return Object.hasOwn(PRIMITIVES, name) ? PRIMITIVES[name] : undefined;
}
/** Une primitive se nomme en PascalCase ; un composant publié par son `artifact_id` (slug pointé). */
export function isPrimitiveName(name) {
    return /^[A-Z][A-Za-z0-9]*$/.test(name);
}
//# sourceMappingURL=primitives.js.map